<template>
  <div class="main-wrap">
    <main-header :name="$t('brand.name')" />
    <banner src="/images/faq.svg" :title="$t('faq.title')" :dec="$t('faq.desc')" />
    <v-container class="space-top-short faq-page-shell">
      <v-row class="faq-grid">
        <v-col class="px-4 px-md-6" md="4" lg="3" cols="12">
          <v-card class="faq-topic-panel rounded-xl pa-5 pa-md-6" elevation="0">
            <topic-list :topics="faq" />
          </v-card>
        </v-col>
        <v-col class="px-4 px-md-6" md="8" lg="9" cols="12">
          <v-card class="faq-content-panel rounded-xl pa-5 pa-md-7" elevation="0">
            <faq-list v-for="(item, index) in faq" :faq="item" :key="index" :id="index" />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <main-footer />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/pages';

.main-wrap {
  --faq-primary: #3f51b5;
}
.faq-page-shell {
  position: relative;
  z-index: 1;
}

.faq-topic-panel,
.faq-content-panel {
  border: 1px solid rgba(63, 81, 181, 0.14);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.88)) !important;
  box-shadow: 0 14px 34px rgba(38, 56, 134, 0.1);
  backdrop-filter: blur(6px);
}

.faq-topic-panel {
  position: sticky;
  top: 110px;
}

::v-deep .faq-topic-panel .use-text-subtitle,
::v-deep .faq-content-panel .use-text-subtitle {
  color: #2a357a;
  font-weight: 800;
}

::v-deep .faq-topic-panel .topic-list li {
  border-bottom: 1px solid rgba(63, 81, 181, 0.14);
  padding: 0.75rem 0;
}

::v-deep .faq-topic-panel .topic-list li a {
  color: #35449b;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

::v-deep .faq-topic-panel .topic-list li a:hover {
  color: #f44336;
}

::v-deep .faq-content-panel .paper {
  border: 1px solid rgba(63, 81, 181, 0.12);
  box-shadow: 0 8px 18px rgba(63, 81, 181, 0.08) !important;
}

::v-deep .faq-content-panel .expanded.v-expansion-panel {
  background: var(--faq-primary) !important;
}

::v-deep .faq-content-panel .heading {
  color: #1f2b6b;
  font-weight: 700;
}

::v-deep .faq-content-panel .expanded .heading {
  color: #ffffff !important;
}

::v-deep .faq-content-panel .expanded .v-icon {
  color: #ffffff !important;
}

::v-deep .faq-content-panel .detail {
  background: #f9faff;
}

@media (max-width: 960px) {
  .faq-topic-panel {
    position: relative;
    top: 0;
    margin-bottom: 1rem;
  }

  .faq-topic-panel,
  .faq-content-panel {
    padding: 1rem !important;
    border-radius: 14px !important;
  }
}

@media (max-width: 600px) {
  .faq-page-shell {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }

  ::v-deep .faq-content-panel .heading {
    font-size: 0.95rem;
  }

  ::v-deep .faq-content-panel pre {
    font-size: 0.92rem;
    line-height: 1.6;
  }
}
</style>

<script>
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FaqList from '@/components/Faq/FaqList';
import Banner from '~/components/About/Banner'
import TopicList from '@/components/Faq/TopicList';

export default {
  components: {
    'main-header': Header,
    'main-footer': Footer,
    TopicList,
    FaqList,
    Banner
  },
  async asyncData({ $axios, params, i18n }) {
    return {
      faq: (await $axios.get(`/file?path=${i18n.t("faq.link")}`))
        .data
    };
  },
  head() {
    const title = this.$t("seo.faq.title");
    const desc = this.$t("seo.faq.desc");

    const faqItems = (this.faq || []).flatMap((topic) =>
      (topic.items || []).map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      }))
    );

    const faqSchema = faqItems.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems,
        }
      : null;

    return {
      title,
      meta: [
        { hid: "description", name: "description", content: desc },
        { hid: "og:title", property: "og:title", content: title },
        { hid: "og:description", property: "og:description", content: desc },
        { hid: "og:url", property: "og:url", content: `https://www.motanamy.com${this.$route.path}` },
        { hid: "twitter:title", name: "twitter:title", content: title },
        { hid: "twitter:description", name: "twitter:description", content: desc },
      ],
      ...(faqSchema && {
        __dangerouslyDisableSanitizers: ["script"],
        script: [
          {
            hid: "faq-schema",
            type: "application/ld+json",
            innerHTML: JSON.stringify(faqSchema),
          },
        ],
      }),
    };
  }
};
</script>
